<script setup lang="ts">
import { ArrowRight, CalendarPlus, MapPinned, Search, Sparkles } from '../components/wx-icons'
import { onMounted, ref } from 'vue'
import ActivityCard from '../components/ActivityCard.vue'
import HomeCourtIllustration from '../components/HomeCourtIllustration.vue'
import StatePanel from '../components/StatePanel.vue'
import VenueCard from '../components/VenueCard.vue'
import { api, listOf, queryString } from '../services/api'

const venues = ref<any[]>([])
const activities = ref<any[]>([])
const loading = ref(true)
const locationHint = ref('正在获取附近内容')

function location(): Promise<{ lat?: number; lng?: number }> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({})
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => resolve({}),
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 },
    )
  })
}

async function load() {
  loading.value = true
  const coords = await location()
  locationHint.value = coords.lat ? '已按你的位置推荐' : '按热门内容推荐'
  const [venueResult, activityResult] = await Promise.allSettled([
    api<any>(`/venues/recommendations${queryString({ ...coords })}`),
    api<any>(`/activity/hot${queryString({ limit: 4, ...coords })}`),
  ])
  if (venueResult.status === 'fulfilled') venues.value = listOf(venueResult.value).slice(0, 4)
  if (activityResult.status === 'fulfilled') activities.value = listOf(activityResult.value).slice(0, 4)
  loading.value = false
}

onMounted(load)
</script>

<template>
  <section class="home-hero">
    <div class="shell home-hero-grid">
      <div class="hero-copy">
        <div class="eyebrow"><Sparkles :size="16" />下一场，从这里开始</div>
        <h1>找场地，<br /><em>也找到球搭子。</em></h1>
        <p>浏览附近网球场与真实球局，在电脑或手机上直接报名，也可以自己发起一场。</p>
        <div class="hero-actions">
          <RouterLink class="button button-primary button-large" to="/find"><Search :size="20" />开始找球</RouterLink>
          <RouterLink class="button button-secondary button-large" to="/activities/new"><CalendarPlus :size="20" />发布活动</RouterLink>
        </div>
        <span class="location-hint"><MapPinned :size="15" />{{ locationHint }}</span>
      </div>
      <div class="hero-court-card" aria-hidden="true">
        <HomeCourtIllustration />
        <div class="floating-card floating-a"><b>18:30</b><span>今晚 · 双打</span></div>
        <div class="floating-card floating-b"><b>3.0–4.0</b><span>还有 2 个名额</span></div>
      </div>
    </div>
  </section>

  <section class="page-section shell">
    <div class="section-heading"><div><span class="section-kicker">COURTS</span><h2>附近值得去的场地</h2></div><RouterLink to="/find?tab=venues">查看全部 <ArrowRight :size="17" /></RouterLink></div>
    <StatePanel v-if="loading" mode="loading" />
    <div v-else-if="venues.length" class="card-grid venue-grid"><VenueCard v-for="venue in venues" :key="venue.id" :venue="venue" /></div>
    <StatePanel v-else mode="empty" title="附近场馆正在整理中" description="你仍可以进入找球页按城市搜索全部场馆。" />
  </section>

  <section class="page-section section-tint">
    <div class="shell">
      <div class="section-heading"><div><span class="section-kicker">ACTIVITIES</span><h2>最近可以加入的球局</h2></div><RouterLink to="/find?tab=activities">查看全部 <ArrowRight :size="17" /></RouterLink></div>
      <div v-if="activities.length" class="activity-list"><ActivityCard v-for="activity in activities" :key="activity.id" :activity="activity" /></div>
      <StatePanel v-else-if="!loading" mode="empty" title="暂时没有近期活动" description="发起一场球局，让附近球友来报名。" />
    </div>
  </section>
</template>
